/**
 * JSON document diff (task 725).
 *
 * Version documents are configuration JSON the engine consumes as JSON, so
 * comparing two versions is a document diff rather than a text diff — which is
 * exactly what falls out of the report's storage model. This flattens both
 * documents to dotted leaf paths and reports the union, so a reader sees
 * "authPolicy.mode changed from apiKey to public" instead of a wall of braces.
 *
 * Arrays are compared by index and treated as leaves when they hold scalars,
 * which keeps a reordered tools list readable without pretending to compute a
 * minimal edit script.
 */

export type JsonDiffKind = 'added' | 'removed' | 'changed';

export interface JsonDiffEntry {
  /** Dotted path to the leaf, e.g. `authPolicy.mode` or `nodes.0.agentId`. */
  path: string;
  kind: JsonDiffKind;
  /** Rendered value from the left (older) document; undefined when added. */
  before?: string;
  /** Rendered value from the right (newer) document; undefined when removed. */
  after?: string;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Render a leaf for display — strings unquoted, everything else as JSON. */
export const renderJsonLeaf = (value: unknown): string => {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

/**
 * Flatten a document to leaf paths. An empty object/array is itself a leaf, so
 * clearing a populated object shows up as a change rather than vanishing.
 */
export const flattenJson = (
  value: unknown,
  prefix = '',
  out: Map<string, unknown> = new Map()
): Map<string, unknown> => {
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      out.set(prefix || '(root)', {});
      return out;
    }
    for (const key of keys) {
      flattenJson(value[key], prefix ? `${prefix}.${key}` : key, out);
    }
    return out;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      out.set(prefix || '(root)', []);
      return out;
    }
    value.forEach((item, index) => {
      flattenJson(item, prefix ? `${prefix}.${index}` : String(index), out);
    });
    return out;
  }

  out.set(prefix || '(root)', value);
  return out;
};

/**
 * Diff two version documents. `before` is the older version, `after` the newer;
 * entries come back in path order so the same field always appears in the same
 * place across two different comparisons.
 */
export const diffJsonDocuments = (
  before: unknown,
  after: unknown
): JsonDiffEntry[] => {
  const left = flattenJson(before ?? {});
  const right = flattenJson(after ?? {});

  const paths = Array.from(new Set([...left.keys(), ...right.keys()])).sort();

  const entries: JsonDiffEntry[] = [];
  for (const path of paths) {
    const hasLeft = left.has(path);
    const hasRight = right.has(path);
    const leftValue = left.get(path);
    const rightValue = right.get(path);

    if (hasLeft && !hasRight) {
      entries.push({ path, kind: 'removed', before: renderJsonLeaf(leftValue) });
      continue;
    }
    if (!hasLeft && hasRight) {
      entries.push({ path, kind: 'added', after: renderJsonLeaf(rightValue) });
      continue;
    }
    if (JSON.stringify(leftValue) !== JSON.stringify(rightValue)) {
      entries.push({
        path,
        kind: 'changed',
        before: renderJsonLeaf(leftValue),
        after: renderJsonLeaf(rightValue),
      });
    }
  }

  return entries;
};
